import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cn } from './cn'
import { Slot } from './Slot'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** none = 0, sm = 12, md = 16 (default), lg = 20. */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** Hover border and soft shadow with a pointer cursor. */
  clickable?: boolean
  /** Render the single child (for example a Link) as the card. */
  asChild?: boolean
  ref?: Ref<HTMLDivElement>
}

const paddings = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-5' }

/** White surface panel (REDESIGN section 4): 1px line, 10px radius and a whisper of shadow. */
export function Card({ padding = 'md', clickable, asChild, className, children, ref, ...rest }: CardProps) {
  const classes = cn(
    'block rounded-card border border-line bg-surface text-text shadow-card',
    paddings[padding],
    clickable &&
      'cursor-pointer transition-[border-color,box-shadow] duration-150 ease-out hover:border-line-strong hover:shadow-hover focus-ring',
    className,
  )
  if (asChild) {
    return (
      <Slot className={classes} ref={ref as Ref<HTMLElement>} {...rest}>
        {children}
      </Slot>
    )
  }
  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  )
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-section text-text', className)} {...rest}>
      {children}
    </h3>
  )
}

export function CardDescription({ className, children, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-meta text-muted', className)} {...rest}>
      {children}
    </p>
  )
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode
  action?: ReactNode
}

/** Title row with an optional action on the right (for example "See all"). */
export function CardHeader({ title, action, className, children, ...rest }: CardHeaderProps) {
  return (
    <div className={cn('mb-3 flex items-center justify-between gap-3', className)} {...rest}>
      {title ? <CardTitle>{title}</CardTitle> : children}
      {action}
    </div>
  )
}
